import React, { useCallback } from 'react';
import { Radio, Slider, Typography, Divider, Card } from 'antd';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
    setSimilarityDisplayMode,
    setWeightA,
    SimilarityDisplayMode,
} from '../redux/slices/settingsSlice';

const { Title, Text } = Typography;

const SettingsPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { similarityDisplayMode, weightA } = useAppSelector((s) => s.settings);

    // ── Font size ────────────────────────────────────────────────────────────

    const getFontSize = useCallback((variable: string, defaultValue = 1): number => {
        const value = getComputedStyle(document.documentElement).getPropertyValue(variable);
        const parsed = parseFloat(value);
        return isNaN(parsed) ? defaultValue : parsed;
    }, []);

    const changeFontSize = useCallback((factor: number) => {
        ['--font-size-base', '--font-size-xl', '--font-size-2xl', '--font-size-3xl'].forEach((v) => {
            document.documentElement.style.setProperty(v, `${getFontSize(v) * factor}rem`);
        });
    }, [getFontSize]);

    // ── Similarity display ───────────────────────────────────────────────────

    const weightB = Math.round((1 - weightA) * 100);
    const weightAPercent = Math.round(weightA * 100);

    const sliderMarks = {
        0:   '0%',
        25:  '25%',
        50:  '50%',
        75:  '75%',
        100: '100%',
    };

    return (
        <div style={{ maxWidth: 600, margin: '0 auto', padding: 24 }}>
            <Title level={3}>Настройки</Title>

            {/* ── Коэффициент близости ─────────────────────────────────────── */}
            <Card style={{ marginBottom: 24 }}>
                <Title level={5}>Коэффициент близости</Title>

                <div style={{ marginBottom: 16 }}>
                    <Text strong>Режим отображения</Text>
                    <br />
                    <Radio.Group
                        style={{ marginTop: 8 }}
                        value={similarityDisplayMode}
                        onChange={(e) =>
                            dispatch(setSimilarityDisplayMode(e.target.value as SimilarityDisplayMode))
                        }
                    >
                        <Radio value="triplet">
                            Триплет <Text type="secondary">(нейросеть A · среднее · нейросеть B)</Text>
                        </Radio>
                        <br />
                        <Radio value="average" style={{ marginTop: 8 }}>
                            Только среднее
                        </Radio>
                    </Radio.Group>
                </div>

                <Divider />

                <div>
                    <Text strong>Веса нейросетей</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Влияние каждой нейросети на итоговый коэффициент (сумма весов = 100%)
                    </Text>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, marginBottom: 4 }}>
                        <Text>Нейросеть A</Text>
                        <Text>Нейросеть B</Text>
                    </div>

                    <Slider
                        min={0}
                        max={100}
                        step={5}
                        marks={sliderMarks}
                        value={weightAPercent}
                        onChange={(v) => dispatch(setWeightA(v / 100))}
                        tooltip={{ formatter: (v) => `A: ${v}% · B: ${100 - (v ?? 0)}%` }}
                    />

                    <div
                        style={{
                            display: 'flex',
                            marginTop: 8,
                            borderRadius: 6,
                            overflow: 'hidden',
                            height: 24,
                            fontSize: 12,
                        }}
                    >
                        <div
                            style={{
                                flex: weightAPercent,
                                background: '#4096ff',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {weightAPercent > 10 ? `${weightAPercent}%` : ''}
                        </div>
                        <div
                            style={{
                                flex: weightB,
                                background: '#52c41a',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {weightB > 10 ? `${weightB}%` : ''}
                        </div>
                    </div>
                </div>
            </Card>

            {/* ── Размер шрифта ────────────────────────────────────────────── */}
            <Card>
                <Title level={5}>Размер шрифта</Title>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={() => changeFontSize(1.1)}
                        style={{ padding: '6px 16px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                    >
                        A+
                    </button>
                    <button
                        onClick={() => changeFontSize(1 / 1.1)}
                        style={{ padding: '6px 16px', background: '#ff4d4f', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                    >
                        A−
                    </button>
                </div>
            </Card>
        </div>
    );
};

export default SettingsPage;
